import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { ApiError, post } from "@/api/client";
import AuthLayout from "@/brand/AuthLayout";

/**
 * Public partner sign-up.
 *
 * Nothing exists until MultiDoc approves the request: the form records who is
 * asking and the approver creates the organisation, its licensing identity and
 * the first invitation in one step. The slug is proposed from the company name
 * and can be edited — it becomes the organisation's short name everywhere.
 */
function slugOf(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export default function PartnerRegisterPage() {
  const [company, setCompany] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("Malaysia");
  const [website, setWebsite] = useState("");
  const [message, setMessage] = useState("");
  const [kind, setKind] = useState("ecm");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugOf(company);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await post<{ id: string; status: string; message: string }>(
        "/resellers/register",
        {
          company_name: company.trim(),
          slug: effectiveSlug,
          contact_name: contactName.trim(),
          contact_email: email.trim(),
          contact_phone: phone.trim(),
          country: country.trim(),
          website: website.trim(),
          message: message.trim(),
          partner_kind: kind,
        },
      );
      setDone(result.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>Request received</Typography>
          <Typography variant="body2" color="text.secondary">{done}</Typography>
          <Typography variant="body2" color="text.secondary">
            Once approved, {email} receives an invitation to set a password and sign in.
          </Typography>
          <Button component={RouterLink} to="/login" variant="outlined">Back to sign in</Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack component="form" onSubmit={submit} spacing={2}>
        <div>
          <Typography variant="h5" fontWeight={600}>Become a partner</Typography>
          <Typography variant="body2" color="text.secondary">
            Resell MultiDoc, run demos for your customers and generate proposals in
            your own brand. MultiDoc reviews every request.
          </Typography>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Company" required value={company} autoFocus
          onChange={(e) => setCompany(e.target.value)}
        />
        <TextField
          label="Short name" required value={effectiveSlug}
          onChange={(e) => { setSlugTouched(true); setSlug(slugOf(e.target.value)); }}
          helperText="Lowercase letters, digits and hyphens; 3 to 40 characters"
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Contact name" required value={contactName} sx={{ flex: 1 }}
            onChange={(e) => setContactName(e.target.value)} autoComplete="name"
          />
          <TextField
            label="Work email" type="email" required value={email} sx={{ flex: 1 }}
            onChange={(e) => setEmail(e.target.value)} autoComplete="email"
          />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Phone" value={phone} sx={{ flex: 1 }}
            onChange={(e) => setPhone(e.target.value)} autoComplete="tel"
          />
          <TextField
            label="Country" value={country} sx={{ flex: 1 }}
            onChange={(e) => setCountry(e.target.value)}
          />
        </Stack>
        <TextField
          label="Website" value={website}
          onChange={(e) => setWebsite(e.target.value)} placeholder="https://"
        />
        <TextField select label="What you resell" value={kind}
                   onChange={(e) => setKind(e.target.value)}>
          <MenuItem value="ecm">MultiDoc ECM</MenuItem>
          <MenuItem value="scanner">Scanners, with MultiImage cloud scanning bundled</MenuItem>
          <MenuItem value="both">Both</MenuItem>
        </TextField>
        <TextField
          label="Tell us about your business" value={message} multiline minRows={2}
          onChange={(e) => setMessage(e.target.value)}
          helperText="Which markets, which customers, which scanner brands"
        />

        <Button
          type="submit" variant="contained" size="large"
          disabled={busy || !company.trim() || !contactName.trim() || !email.trim()
                    || effectiveSlug.length < 3}
        >
          {busy ? "Sending…" : "Request partner access"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Already a partner? <RouterLink to="/login">Sign in</RouterLink>
        </Typography>
      </Stack>
    </AuthLayout>
  );
}
